import { FC } from 'react';
import { FieldArray, useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import { Input } from '../common/form/Input';
import { QcsSwitch } from '../common/form/QcsSwitch';
import { FieldWithAction } from '../common/form/FieldWithAction';
import { QcsButton } from '../common/basic/QcsButton';
import { QcsTypography } from '../common/basic/QcsTypography';
import QcsAlert from '../common/basic/QcsAlert';
import { MAX_IP_RANGES } from './integrationUtils';

/** Any IP address by default; restricting takes an explicit choice. The switch goes first, so showing the list never moves it from under the cursor. */
export const IntegrationIpRanges: FC = () => {
  const { t } = useTranslation();
  const { values, setFieldValue } = useFormikContext<{ restrictIps: boolean; ipRanges: string[] }>();

  return (
    <FieldArray name="ipRanges">
      {({ push, remove }) => (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
            <QcsSwitch
              name="restrictIps"
              label={t('integration.apiAccess.ipRanges.restrict')}
              //Turning it on is to enter an address, so there's a field ready for it.
              onChange={(checked) => checked && values.ipRanges.length === 0 && setFieldValue('ipRanges', [''])}
            />
            {!values.restrictIps && (
              <QcsAlert severity="warning" compact>
                {t('integration.apiAccess.ipRanges.noRestrictionWarning')}
              </QcsAlert>
            )}
          </Box>
          {values.restrictIps && (
            <>
              <QcsTypography variant="body2">{t('integration.apiAccess.ipRanges.description')}</QcsTypography>
              {values.ipRanges.map((_, index) => (
                <FieldWithAction
                  key={index}
                  action={
                    //The last address stays - the switch is what removes the restriction.
                    <QcsButton onClick={() => remove(index)} disabled={values.ipRanges.length === 1} sx={{ mt: 1 }}>
                      <DeleteForeverIcon color={values.ipRanges.length === 1 ? 'disabled' : 'error'} />
                    </QcsButton>
                  }
                >
                  <Input name={`ipRanges.${index}`} label={t('integration.apiAccess.ipRanges.label')} fullWidth required removeSpaces />
                </FieldWithAction>
              ))}
              <QcsButton
                variant="outlined"
                onClick={() => push('')}
                //Another field only once all the ones above are filled in.
                disabled={values.ipRanges.length >= MAX_IP_RANGES || values.ipRanges.some((x) => !x.trim())}
                sx={{ alignSelf: 'flex-start' }}
              >
                {t('integration.apiAccess.ipRanges.add')}
              </QcsButton>
            </>
          )}
        </Box>
      )}
    </FieldArray>
  );
};
